import { Package } from 'lucide-react';

import { ImageLightbox } from '@/components/molecules/image-lightbox';
import { getImageUrl } from '@/lib/utils';
import type { ImageObject } from '@/lib/types';

type ImageInput = string | ImageObject | null | undefined;

interface ProductImageGalleryProps {
  images: ImageInput[];
  productName: string;
  selectedIndex: number;
  lightboxOpen: boolean;
  onOpenLightbox: (index: number) => void;
  onCloseLightbox: () => void;
  onChangeIndex: (index: number) => void;
}

export function ProductImageGallery({
  images,
  productName,
  selectedIndex,
  lightboxOpen,
  onOpenLightbox,
  onCloseLightbox,
  onChangeIndex,
}: ProductImageGalleryProps) {
  const imageUrls = Array.isArray(images)
    ? images.map((img) => getImageUrl(img as ImageInput)).filter(Boolean) as string[]
    : [];
  const mainImage = imageUrls[0] || null;

  return (
    <div className="space-y-3 md:space-y-4 min-w-0">
      <div
        className="aspect-[4/3] md:aspect-square rounded-lg overflow-hidden bg-muted cursor-pointer hover:ring-2 hover:ring-primary transition-all"
        onClick={() => onOpenLightbox(0)}
      >
        {mainImage ? (
          <img
            src={mainImage}
            alt={productName}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="flex h-full items-center justify-center text-muted-foreground">
            <Package className="size-16" />
          </div>
        )}
      </div>

      {imageUrls.length > 1 && (
        <div className="grid grid-cols-4 gap-2">
          {imageUrls.slice(0, 4).map((imageUrl, index) => (
            <div
              key={index}
              className="aspect-square rounded-lg overflow-hidden bg-muted cursor-pointer hover:ring-2 hover:ring-primary transition-all"
              onClick={() => onOpenLightbox(index)}
            >
              <img
                src={imageUrl}
                alt={`${productName} ${index + 1}`}
                className="w-full h-full object-cover"
              />
            </div>
          ))}
        </div>
      )}

      <ImageLightbox
        images={imageUrls}
        selectedIndex={selectedIndex}
        open={lightboxOpen}
        onClose={onCloseLightbox}
        onChangeIndex={onChangeIndex}
        productName={productName}
      />
    </div>
  );
}
